import { useMemo, useState } from 'react';
import {
  IProductSizeSelect,
  ISizeOption,
} from 'pages/ProductDetail/hooks/types';
import { useProductSizeSort } from 'pages/ProductDetail/hooks/useProductSizeSort';
import { useTranslations } from 'modules/i18n/useTranslations';
import { usePathname, useRouter } from 'modules/next/hooks';
import {
  IAdaptedBasicProduct,
  IAdaptedListProduct,
} from 'commerce/scayle/utils/adapter/types';

export const useProductSizeSelect = (
  product: IAdaptedBasicProduct | IAdaptedListProduct,
  referenceKey: string,
  ignoreRedirect?: boolean,
  sortByNumber?: boolean,
): IProductSizeSelect => {
  const t = useTranslations();
  const { sizeSort, numericSort } = useProductSizeSort();

  /** Digital Products may be sold without stock */
  const isSellableWithoutStock =
    product?.variants?.[0]?.stock?.isSellableWithoutStock;

  const router = useRouter();
  const pathname = usePathname();

  const hasSizes = Number(product.size?.length) > 1;
  const defaultLimit = product.limitForCart || 10;

  const [selectedVariant, setSelectedVariant] = useState<string>(
    hasSizes && product.referenceKey === referenceKey ? '' : referenceKey,
  );

  const [inStockVariant, setInStockVariant] = useState(defaultLimit);
  const [isSizeSelectVisible, setIsSizeSelectVisible] = useState(false);

  const quantityLimit =
    inStockVariant > defaultLimit ? defaultLimit : inStockVariant;

  const sizeOptions: ISizeOption[] = useMemo(() => {
    const sizes = product?.size || [];

    return [
      {
        value: '',
        label: t('product.pdp.select') || 'select',
        disabled: true,
      },
      ...sizes
        .map((size) => ({
          value: size.referenceKey,
          label: size.label?.trim(),
          disabled: !size.stock && !isSellableWithoutStock,
        }))
        .sort(sortByNumber ? numericSort : sizeSort),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.size]);

  const toggleSizeSelectVisibility = () => {
    setIsSizeSelectVisible(hasSizes ? !isSizeSelectVisible : false);
  };

  return {
    hasSizes,
    selectedVariant,
    setSelectedVariant: (variantReferenceKey: string) => {
      if (hasSizes && !!variantReferenceKey) {
        const inStock = product.size.find(
          (val) => val.referenceKey === selectedVariant,
        )?.stock;

        inStock && setInStockVariant(inStock);

        const newUrl = product.href.replace(
          product.referenceKey,
          variantReferenceKey,
        );

        if (pathname !== newUrl && !ignoreRedirect) {
          router.push(newUrl);
        }
      }

      setSelectedVariant(variantReferenceKey);
    },
    isSizeSelectVisible,
    setIsSizeSelectVisible,
    quantityLimit,
    sizeOptions,
    toggleSizeSelectVisibility,
  };
};
